import { useEffect, useRef } from 'react'
import { trackEvent } from '../utils/umami'

/**
 * Fires a one-shot `section-view` analytics event the first time a marketing
 * section scrolls into view, so we can see how far down the page people get.
 *
 * Salvaged from the retired standalone `landing/` app, where it was the only
 * thing that did not survive the port into the app shell.
 */
export function useTrackVisibility<T extends HTMLElement = HTMLElement>(sectionName: string) {
  const ref = useRef<T>(null)
  const tracked = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Environments without the API simply do not report section views; the
    // page itself must never depend on this running.
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked.current) {
          tracked.current = true
          trackEvent('section-view', { section: sectionName })
          observer.disconnect()
        }
      },
      { threshold: 0.3 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [sectionName])

  return ref
}
